import React, { useCallback, useState, useEffect } from "react";
import {
  getDelayFeedback,
  getFilterCutoff,
  getFilterType,
  setDelayMix,
  setReverbMix,
  setDelayFeedback,
  setDelayTime,
  setReverbDecay,
  setFilterCutoff,
  setFilterType,
  getDelayMix,
  getDelayTime,
  getReverbDecay,
  getReverbMix,
  getReverseMix,
  setReverseMix,
} from "../utils/audioUtils";
import { FXType } from "./FXDrawer";

const FILTER_MIN = 20;
const FILTER_MAX = 10000;

export interface FilterControlProps {
  filterCutoff: number;
  onFilterCutoffChange: (value: number) => void;
  filterType: BiquadFilterType;
  onFilterTypeChange: (type: BiquadFilterType) => void;
  onFXSelect: (fx: FXType) => void;
  mappingMode?: string | null;
  fxModes?: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };
  onMappingModeChange?: (mode: string | null) => void;
  onFxModeChange?: (modes: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  }) => void;
  onInitializeStepsWithGlobalValue?: (
    fxType: string,
    globalValue: number
  ) => void;
  hasMappingApplied?: boolean;
}

export interface OtherFXControlsProps {
  onFXSelect: (fx: FXType) => void;
  reverbMix: number;
  delayMix: number;
  reverseMix: number;
  onReverbMixChange: (value: number) => void;
  onDelayMixChange: (value: number) => void;
  onReverseMixChange: (value: number) => void;
  mappingMode?: string | null;
  fxModes?: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };
  onMappingModeChange?: (mode: string | null) => void;
  onFxModeChange?: (modes: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  }) => void;
  onInitializeStepsWithGlobalValue?: (
    fxType: string,
    globalValue: number
  ) => void;
  hasMappingApplied?: (fxType: string) => boolean;
}

export interface FXDrawerProps {
  isOpen: boolean;
  selectedFX: FXType;
  onClose: () => void;
  filterCutoff: number;
  onFilterCutoffChange: (value: number) => void;
  reverbDecay: number;
  onReverbDecayChange: (value: number) => void;
  delayTime: string; // Tone.js notation (e.g., "4n", "8n")
  delayFeedback: number;
  onDelayTimeChange: (value: string) => void;
  onDelayFeedbackChange: (value: number) => void;
}

interface AudioControlsContainerProps {
  mappingMode?: string | null;
  fxModes?: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };
  onMappingModeChange?: (mode: string | null) => void;
  onFxModeChange?: (modes: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  }) => void;
  onInitializeStepsWithGlobalValue?: (
    fxType: string,
    globalValue: number
  ) => void;
  hasMappingApplied?: (fxType: string) => boolean;
  refreshTrigger?: number; // Trigger to refresh UI values from audio engine
  children: (
    filterProps: FilterControlProps,
    otherFXProps: OtherFXControlsProps,
    drawerProps: FXDrawerProps
  ) => React.ReactNode;
}

const AudioControlsContainer: React.FC<AudioControlsContainerProps> = ({
  mappingMode,
  fxModes,
  onMappingModeChange,
  onFxModeChange,
  onInitializeStepsWithGlobalValue,
  hasMappingApplied,
  refreshTrigger,
  children,
}) => {
  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedFX, setSelectedFX] = useState<FXType>(null);

  // State for reverb parameters
  const [reverbDecay, setReverbDecayValue] = useState<number>(getReverbDecay());

  // State for delay parameters
  const [delayTime, setDelayTimeValue] = useState<string>(getDelayTime());
  const [delayFeedback, setDelayFeedbackValue] = useState<number>(
    getDelayFeedback()
  );

  // State for audio parameters
  const [filterCutoff, setFilterCutoffValue] = useState<number>(
    getFilterCutoff()
  );
  const [filterType, setFilterTypeValue] = useState<BiquadFilterType>(
    getFilterType()
  );

  // State for real-time ramped values
  // For cutoff, 0 = lowest freq (20Hz), 1 = highest freq (10000Hz) - using logarithmic mapping
  const initialCutoff = getFilterCutoff();
  const initialLogValue =
    Math.log(initialCutoff / FILTER_MIN) / Math.log(FILTER_MAX / FILTER_MIN);
  const [realtimeFilterMix, setRealtimeFilterMix] = useState<number>(
    Math.max(0, Math.min(1, initialLogValue))
  );
  const [realtimeReverbMix, setRealtimeReverbMix] = useState<number>(
    getReverbMix()
  );
  const [realtimeDelayMix, setRealtimeDelayMix] = useState<number>(
    getDelayMix()
  );
  const [realtimeReverseMix, setRealtimeReverseMix] = useState<number>(
    getReverseMix()
  );

  // Refresh all UI values when refreshTrigger changes (e.g., after preset load)
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      // Refresh all parameter values from audio engine
      setReverbDecayValue(getReverbDecay());
      setDelayTimeValue(getDelayTime());
      setDelayFeedbackValue(getDelayFeedback());
      setFilterCutoffValue(getFilterCutoff());
      setFilterTypeValue(getFilterType());
      // Update realtime filter mix using logarithmic mapping
      const currentCutoff = getFilterCutoff();
      const logValue =
        Math.log(currentCutoff / FILTER_MIN) /
        Math.log(FILTER_MAX / FILTER_MIN);
      setRealtimeFilterMix(Math.max(0, Math.min(1, logValue)));
      setFilterTypeValue(getFilterType());
      setRealtimeReverbMix(getReverbMix());
      setRealtimeDelayMix(getDelayMix());
      setRealtimeReverseMix(getReverseMix());
    }
  }, [refreshTrigger]);

  // Handle FX selection (opens drawer)
  const handleFXSelect = useCallback((fx: FXType) => {
    setSelectedFX(fx);
    setIsDrawerOpen(true);
  }, []);

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedFX(null);
  }, []);

  // Handle filter cutoff change
  const handleFilterCutoffChange = useCallback((value: number) => {
    setFilterCutoffValue(value);
    setFilterCutoff(value);
    // Update realtime mix value when manually changing cutoff
    // Convert frequency (20Hz-10000Hz) to UI range (0-1) using logarithmic mapping
    // Note: 0 = lowest frequency, 1 = highest frequency
    const logValue =
      Math.log(value / FILTER_MIN) / Math.log(FILTER_MAX / FILTER_MIN);
    setRealtimeFilterMix(Math.max(0, Math.min(1, logValue)));
  }, []);

  // Handle filter type change
  const handleFilterTypeChange = useCallback((type: BiquadFilterType) => {
    setFilterTypeValue(type);
    setFilterType(type);
  }, []);

  // Handle reverb mix change
  const handleReverbMixChange = useCallback((value: number) => {
    setReverbMix(value);
    // Update realtime value when manually changing mix
    setRealtimeReverbMix(value);
  }, []);

  // Handle reverb decay change
  const handleReverbDecayChange = useCallback((value: number) => {
    setReverbDecayValue(value);
    setReverbDecay(value);
  }, []);

  // Handle delay time change
  const handleDelayTimeChange = useCallback((value: string) => {
    setDelayTimeValue(value);
    setDelayTime(value);
  }, []);

  // Handle delay feedback change
  const handleDelayFeedbackChange = useCallback((value: number) => {
    setDelayFeedbackValue(value);
    setDelayFeedback(value);
  }, []);

  // Handle delay mix change
  const handleDelayMixChange = useCallback((value: number) => {
    setDelayMix(value);
    // Update realtime value when manually changing mix
    setRealtimeDelayMix(value);
  }, []);

  // Handle reverse mix change
  const handleReverseMixChange = useCallback((value: number) => {
    setReverseMix(value);
    setRealtimeReverseMix(value);
  }, []);

  // Convert realtimeFilterMix (0-1) to Hz using logarithmic mapping
  const currentFilterCutoffHz =
    FILTER_MIN * Math.pow(FILTER_MAX / FILTER_MIN, realtimeFilterMix);

  const filterProps: FilterControlProps = {
    filterCutoff: currentFilterCutoffHz,
    onFilterCutoffChange: handleFilterCutoffChange,
    filterType: filterType,
    onFilterTypeChange: handleFilterTypeChange,
    onFXSelect: handleFXSelect,
    mappingMode,
    fxModes,
    onMappingModeChange,
    onFxModeChange,
    onInitializeStepsWithGlobalValue,
    hasMappingApplied: hasMappingApplied ? hasMappingApplied("filter") : false,
  };

  // Props for OtherFXControls
  const otherFXProps: OtherFXControlsProps = {
    onFXSelect: handleFXSelect,
    reverbMix: realtimeReverbMix,
    delayMix: realtimeDelayMix,
    reverseMix: realtimeReverseMix,
    onReverbMixChange: handleReverbMixChange,
    onDelayMixChange: handleDelayMixChange,
    onReverseMixChange: handleReverseMixChange,
    mappingMode,
    fxModes,
    onMappingModeChange,
    onFxModeChange,
    onInitializeStepsWithGlobalValue,
    hasMappingApplied,
  };

  // Props for FXDrawer
  const drawerProps: FXDrawerProps = {
    isOpen: isDrawerOpen,
    selectedFX: selectedFX,
    onClose: handleDrawerClose,
    filterCutoff: filterCutoff,
    onFilterCutoffChange: handleFilterCutoffChange,
    reverbDecay: reverbDecay,
    onReverbDecayChange: handleReverbDecayChange,
    delayTime: delayTime,
    delayFeedback: delayFeedback,
    onDelayTimeChange: handleDelayTimeChange,
    onDelayFeedbackChange: handleDelayFeedbackChange,
  };

  return <>{children(filterProps, otherFXProps, drawerProps)}</>;
};

export default AudioControlsContainer;
